import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Lock as LockIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { purchaseProjectAccess, ProjectAccessRequest } from '../lib/api/projectAccess';
import { getUserCredits } from '../lib/api/promotionBids';

interface ProjectAccessModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectTitle: string;
  onAccessGranted: () => void;
}

const ProjectAccessModal: React.FC<ProjectAccessModalProps> = ({
  open,
  onClose,
  projectId,
  projectTitle,
  onAccessGranted
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  
  const ACCESS_COST = 3; // 3 credits to access project details

  // Load user credits when modal opens
  useEffect(() => {
    if (open) {
      loadUserCredits();
    }
  }, [open]);

  const loadUserCredits = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const credits = await getUserCredits();
      setUserCredits(credits);
    } catch (err: any) {
      setError(err.message || 'Грешка при зареждане на кредитите');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (userCredits < ACCESS_COST) {
      setError(`Нямате достатъчно кредити. Нужни: ${ACCESS_COST}, налични: ${userCredits}`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const request: ProjectAccessRequest = {
        project_id: projectId,
        credits_spent: ACCESS_COST
      };

      await purchaseProjectAccess(request);
      
      setSuccess(true);
      onAccessGranted();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Грешка при закупуване на достъпа');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
          border: '1px solid rgba(40, 160, 240, 0.3)',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ 
        color: '#ffffff', 
        textAlign: 'center',
        fontSize: '1.5rem',
        fontWeight: 700,
        pb: 1
      }}>
        Закупуване на достъп
      </DialogTitle>

      <DialogContent sx={{ color: '#ffffff' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#28A0F0' }} />
          </Box>
        ) : success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: '#10b981', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
              Достъпът е закупен успешно!
            </Typography>
            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
              Сега можете да видите пълните детайли на проекта.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 3, color: '#d0d0d0', textAlign: 'center' }}>
              За да видите пълното описание и контактните данни на проекта "{projectTitle}", 
              трябва да закупите достъп с кредити.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Project Access Info */}
            <Card sx={{ 
              mb: 3, 
              background: 'rgba(40, 160, 240, 0.1)',
              border: '1px solid rgba(40, 160, 240, 0.3)'
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#28A0F0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityIcon sx={{ fontSize: '1.2rem' }} />
                  Какво получавате:
                </Typography>
                
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: '1rem', color: '#10b981' }} />
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      Пълно описание на проекта
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: '1rem', color: '#10b981' }} />
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      Контактни данни на клиента
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: '1rem', color: '#10b981' }} />
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      Детайлни изисквания
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: '1rem', color: '#10b981' }} />
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      Възможност за кандидатстване
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Cost Information */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 2,
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              mb: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Цена за достъп:
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ color: '#28A0F0', fontWeight: 700 }}>
                {ACCESS_COST} кредита
              </Typography>
            </Box>

            {/* User Credits */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 2,
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              mb: 3
            }}>
              <Typography variant="body1" sx={{ color: '#a0a0a0' }}>
                Ваши кредити:
              </Typography>
              <Typography variant="h6" sx={{ color: userCredits >= ACCESS_COST ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                {userCredits} кредита
              </Typography>
            </Box>

            {userCredits < ACCESS_COST && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Нямате достатъчно кредити за закупуване на достъпа. 
                Можете да закупите допълнителни кредити от профила си.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      {!loading && !success && (
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={onClose}
            sx={{
              color: '#a0a0a0',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            Отказ
          </Button>
          <Button
            onClick={handlePurchase}
            disabled={submitting || userCredits < ACCESS_COST}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <LockIcon />}
            sx={{
              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
              color: '#ffffff',
              px: 3,
              py: 1,
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            {submitting ? 'Закупуване...' : `Закупи достъп за ${ACCESS_COST} кредита`}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ProjectAccessModal;
